import { createAsyncThunk } from '@reduxjs/toolkit'
import { getMissionApi, getMissionDataApi } from '../../api'
import type { Mission, MissionData, MissionProgress } from './types'

export interface GetMissionPayload {
  mission: Mission | null
  missionProgress: MissionProgress | null
  walletAddress: string
}

/** Fetch current mission (no day) or mission for a specific day. */
export const getMission = createAsyncThunk<
  GetMissionPayload,
  { day?: number },
  { rejectValue: string }
>(
  'mission/getMission',
  async ({ day }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { profile?: { walletAddress?: string } }
      const walletFromState = state.profile?.walletAddress
      const params = day !== undefined && day !== null ? { day } : undefined

      let mission: Mission | undefined
      let missionProgress: MissionProgress | undefined
      let walletAddress: string | undefined
      try {
        const res = await getMissionApi(params)
        mission = res.mission
        missionProgress = res.missionProgress ?? undefined
        walletAddress = res.walletAddress || walletFromState || ''
      } catch (err: any) {
        if (err?.response?.status === 404 && params?.day != null) {
          walletAddress = (err?.response?.data?.walletAddress as string) || walletFromState || ''
        } else {
          throw err
        }
      }

      return {
        mission: mission ?? null,
        missionProgress: missionProgress ?? null,
        walletAddress: walletAddress || walletFromState || ''
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to fetch mission'
      return rejectWithValue(message)
    }
  }
)

export interface GetMissionDataPayload {
  missionData?: MissionData | null
  missionDataList?: MissionData[] | null
}

/** Fetch mission data (templates). Optional day for one day; no day = all. Fetched when needed. */
export const getMissionData = createAsyncThunk<
  GetMissionDataPayload,
  { day?: number } | { loadAll?: boolean },
  { rejectValue: string }
>(
  'mission/getMissionData',
  async (arg, { rejectWithValue }) => {
    try {
      const params = 'day' in arg && arg.day != null ? { day: arg.day } : {}
      const res = await getMissionDataApi(params)

      if (res.missionData) {
        return { missionData: res.missionData }
      }
      if (res.missionDataList) {
        const list = Array.isArray(res.missionDataList) ? res.missionDataList : [res.missionDataList]
        return {
          missionDataList: list as MissionData[]
        }
      }
      return {}
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to fetch mission data'
      return rejectWithValue(message)
    }
  }
)
